import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';

interface MetricCardProps {
  label: string;
  value: string;
  icon: ReactNode;
  iconColor: string;
  isLoading?: boolean;
  testId?: string;
}

const cardSx: SxProps<Theme> = {
  height: '100%',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  borderColor: 'divider',
  '&:hover': {
    borderColor: 'text.secondary',
    boxShadow: theme =>
      theme.palette.mode === 'dark'
        ? '0 2px 12px rgba(0,0,0,0.3)'
        : '0 2px 12px rgba(0,0,0,0.08)',
  },
};

export function MetricCard({
  label,
  value,
  icon,
  iconColor,
  isLoading,
  testId,
}: MetricCardProps) {
  return (
    <Card variant="outlined" sx={cardSx} data-testid={testId}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${iconColor}22`,
              color: iconColor,
              flexShrink: 0,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {isLoading ? (
              <>
                <Skeleton width={80} height={16} sx={{ mb: 0.5 }} />
                <Skeleton width={100} height={28} />
              </>
            ) : (
              <>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ lineHeight: 1.2, display: 'block' }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ lineHeight: 1.3 }}
                >
                  {value}
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
